import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, throwError } from 'rxjs';
import {
  buildModuleOnboardingSubmitPayload,
  extractOnboardingSubmitErrorMessage,
  extractOnboardingSubmitSuccessMessage,
  isOnboardingSubmitLegacySuccess,
  normalizeOnboardingSubmitPayload,
} from '../../helpers/onboarding-submit.util';
import {
  OnboardingProgressModuleKey,
  OnboardingProgressService,
  OnboardingVesselContext,
} from '../onboarding-progress/onboarding-progress.service';
import { RequestFormService } from '../requestForm/request-form.service';

@Injectable({
  providedIn: 'root',
})
export class OnboardingSubmitService {
  constructor(
    private readonly requestFormService: RequestFormService,
    private readonly onboardingProgressService: OnboardingProgressService,
  ) {}

  /**
   * POST /scperformance/onboarding/submit — final step after module data is saved.
   */
  submitOnboardingPayload(
    payload: Record<string, unknown>,
    file?: File,
  ): Observable<unknown> {
    const body = normalizeOnboardingSubmitPayload(payload);
    return this.requestFormService.submitOnboarding(body, file).pipe(
      catchError((err: unknown) => {
        if (isOnboardingSubmitLegacySuccess(err)) {
          return of((err as { error?: unknown })?.error);
        }
        return throwError(() => err);
      }),
    );
  }

  submitModuleOnboarding(
    module: OnboardingProgressModuleKey,
    options: {
      payload?: Record<string, unknown>;
      moduleData?: Record<string, unknown> | null;
      vesselContext?: Partial<OnboardingVesselContext>;
      file?: File;
      successMessage?: string;
    } = {},
  ): Observable<string> {
    const payload =
      options.payload ??
      buildModuleOnboardingSubmitPayload(module, {
        draft: this.onboardingProgressService.getFormDraft(),
        vesselContext:
          options.vesselContext ?? this.resolveVesselContext(),
        moduleData: options.moduleData,
      });

    return this.submitOnboardingPayload(payload, options.file).pipe(
      map((response) =>
        extractOnboardingSubmitSuccessMessage(
          response,
          options.successMessage ?? 'Onboarding submitted successfully',
        ),
      ),
    );
  }

  resolveVesselContext(imo?: string): Partial<OnboardingVesselContext> {
    const normalizedImo = String(imo ?? '')
      .replace(/\D/g, '')
      .trim();
    const draft = this.onboardingProgressService.getFormDraft();
    const formValue = (draft?.formValue ?? {}) as Record<string, unknown>;
    const session = normalizedImo
      ? this.onboardingProgressService.getSession(normalizedImo)
      : null;

    return {
      vessel_name: String(
        session?.vessel_name ?? formValue['ship'] ?? '',
      ).trim(),
      imo: normalizedImo || String(formValue['imoNumber'] ?? '').replace(/\D/g, '').trim(),
      vessel_type: String(
        session?.vessel_type ?? formValue['typeOfShip'] ?? '',
      ),
      segment: String(session?.segment ?? formValue['segment'] ?? ''),
      client_id: Number(session?.client_id ?? formValue['client'] ?? 0),
    };
  }

  isLegacySuccess(err: unknown): boolean {
    return isOnboardingSubmitLegacySuccess(err);
  }

  extractSuccessMessage(
    response: unknown,
    fallback?: string,
  ): string {
    return extractOnboardingSubmitSuccessMessage(response, fallback);
  }

  extractErrorMessage(err: unknown, fallback?: string): string {
    return extractOnboardingSubmitErrorMessage(err, fallback);
  }
}
